import { DetailHeader } from "../../../components/detailHeader/DetailHeader";
import { Box, IconButton, Typography } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import Grid from "@mui/material/Unstable_Grid2";
import { RecordContext } from "../../../components/record/Record";
import { useContext } from "react";
import { OpenInNew } from "@mui/icons-material";
import { formatDateWithTime } from "../../../utils/dateUtils";
import { differenceInDays, parseISO } from "date-fns";
import { Actions } from "../../../components/actionsMenu/ActionsMenu";
import { useUser } from "../../../api/useAuth";
import { isRoleAdmin } from "../../../utils/rolesUtils";
import { EDIT_LOCK_DAYS_3 } from "../../../utils/constants";
import { useFieldSelectOptions } from "../../../utils/useFieldSelectOptions";

type PsyEvaluationHeaderProps = {
	actions?: Actions;
};

export const PsyEvaluationHeader = ({ actions }: PsyEvaluationHeaderProps) => {
	const recordContext = useContext(RecordContext);
	const location = useLocation();
	const { data: user } = useUser();
	const statusChoices = useFieldSelectOptions("instruments_responses", "status");

	if (!recordContext) return null;

	const isDetail = location.pathname.split("/").pop() === "detail";
	const treatmentId = recordContext.item?.treatment?.id;
	const deleteRedirect = isDetail && treatmentId ? { forceRoute: `treatments/${treatmentId}/psychology` } : undefined;

	const isAdmin = isRoleAdmin(user?.role?.id);

	const instrumentName = recordContext.item?.instrument_id?.name;

	const status = recordContext.item?.status;
	const statusLabel = statusChoices.find((choice) => choice.value === status)?.text ?? status;

	const completedAt = recordContext.item?.completed_at;
	const isEditTimeExpired = completedAt ? differenceInDays(new Date(), parseISO(completedAt)) > EDIT_LOCK_DAYS_3 : false;

	return (
		<DetailHeader
			collection={recordContext.collection}
			actions={actions}
			deleteOnUpdate={deleteRedirect}
			editPermission={{ canEdit: isAdmin || !isEditTimeExpired, tooltip: `Edição bloqueada ${EDIT_LOCK_DAYS_3} dias após a conclusão.` }}
		>
			<Typography variant="h6" color="secondary">
				Avaliação Psicológica
			</Typography>
			<Grid container alignItems="flex-start" display="flex" flexWrap="nowrap" flexDirection="row" gap={5}>
				<ClientOrLeadHeading treatment={recordContext.item?.treatment} lead={recordContext.item?.lead} />
				{instrumentName && (
					<Box>
						<Typography variant="h6" color="primary">
							Instrumento
						</Typography>
						<Typography variant="body1">{instrumentName}</Typography>
					</Box>
				)}
				<Box>
					<Typography variant="h6" color="primary">
						Estado
					</Typography>
					<Typography variant="body1">{statusLabel}</Typography>
				</Box>
				<EvaluationDateBlock label="Enviado" isoDate={recordContext.item?.sent_at} />
				<EvaluationDateBlock label="Iniciado" isoDate={recordContext.item?.started_at} />
				<EvaluationDateBlock label="Concluído" isoDate={recordContext.item?.completed_at} />
			</Grid>
		</DetailHeader>
	);
};

type ClientOrLeadHeadingProps = {
	treatment: any;
	lead: any;
};

const ClientOrLeadHeading = ({ treatment, lead }: ClientOrLeadHeadingProps) => {
	const client = treatment?.client;

	let link: string;
	let primaryLabel: string;
	let secondaryLabel: string;

	if (client) {
		link = `/treatments/${treatment.id}/detail`;
		primaryLabel = client.client_id;
		secondaryLabel = `${client.user_id?.first_name ?? ""} ${client.user_id?.last_name ?? ""}`.trim();
	} else if (lead) {
		link = `/leads/${lead.id}/detail`;
		primaryLabel = lead.name || `${lead.first_name ?? ""} ${lead.last_name ?? ""}`.trim();
		secondaryLabel = lead.email ?? "";
	} else {
		return null;
	}

	return (
		<Box>
			<Box sx={{ display: "flex", alignItems: "center" }}>
				<Typography variant="h6" color="primary" component={Link} sx={{ textDecoration: "none" }} to={link}>
					{primaryLabel}
				</Typography>
				<IconButton sx={{ ml: 2, p: 0 }} color="primary" component={Link} to={link} target="_blank">
					<OpenInNew />
				</IconButton>
			</Box>
			{secondaryLabel && <Typography variant="body1">{secondaryLabel}</Typography>}
		</Box>
	);
};

type EvaluationDateBlockProps = {
	label: string;
	isoDate: string | null | undefined;
};

const EvaluationDateBlock = ({ label, isoDate }: EvaluationDateBlockProps) => {
	if (!isoDate) return null;
	return (
		<Box>
			<Typography variant="h6" color="primary">{label}</Typography>
			<Typography variant="body1">{formatDateWithTime(isoDate)}</Typography>
		</Box>
	);
};
